import { getProbandState } from './probandState';

/**
 * Whether a node may be deleted, and why not when it may not.
 *
 * There were two copies of this rule: the context menu hid Delete Person, and the
 * keyboard Delete shortcut in `ChartBox` re-implemented the same three checks by
 * hand under a comment saying it "mirrors" the menu. Two hand-maintained copies of
 * the rule that decides whether a clinical record can be destroyed is one too
 * many, so TGCLOUD-1644 extracted it.
 *
 * The reasons are user-facing: TGCLOUD-1644 also stops the menu greying items out
 * with no explanation, so a caller that suppresses the action can say why.
 */
export const DELETE_BLOCKED = {
  proxy: 'Use Remove Proxy to delete a proxy',
  proband: 'Move or remove the proband designation first'
};

/**
 * @param {object} node    the selected node (laid-out copy is fine)
 * @param {Array}  members the member records the chart is drawn from
 * @returns {{ allowed: boolean, reason: string }}
 */
export const canDeleteNode = (node, members) => {
  if (!node) return { allowed: false, reason: '' };

  // A proxy is a second drawing of somebody already on the chart. Deleting it
  // must not delete the person — that is what Remove Proxy is for.
  if (node.isProxy) return { allowed: false, reason: DELETE_BLOCKED.proxy };

  // Deleting the active proband is a deliberate two-step flow. Note (D2, accepted
  // when the tri-state proband selector landed) that virtually every structural
  // root reads as the current proband, so this covers more nodes than it looks
  // like it does.
  const activeProbandPid = getProbandState(members).probandPid;
  if (
    activeProbandPid != null &&
    String(node.pid) === String(activeProbandPid)
  ) {
    return { allowed: false, reason: DELETE_BLOCKED.proband };
  }

  return { allowed: true, reason: '' };
};
